import { FC, ReactNode, useEffect, useRef, useState } from "react";
import { Box, Tooltip, TextField, InputAdornment } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import ReplayIcon from "@mui/icons-material/Replay";
import LockResetIcon from "@mui/icons-material/LockReset";
import SaveIcon from "@mui/icons-material/Save";
import UndoIcon from "@mui/icons-material/Undo";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import DropDown from "@/components/forms/inputs/dropDown";
import { useTranslation } from "react-i18next";
import { MuiTelInput, matchIsValidTel } from "mui-tel-input";
import { emailValidationRegex } from "@/helper/regex";
import {
  normalizePhoneForComparison,
  normalizePastedPhoneNumber,
  inheritedValueToSubjectIdListOptions,
  inheritedValueToWzCodeIdListOptions,
  blurNumberInputOnWheel,
} from "@/components/anchorDataOverview/anchorDataOverviewUtils";
import { SubjectSearchSelect } from "@/components/subjectSearch/subjectSearchSelect";
import AnchorDataOverviewDatePicker from "@/components/anchorDataOverview/anchorDataOverviewDatePicker";
import styles from "@/components/anchorDataOverview/anchorDataOverview.module.css";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";

type AnchorDataOverviewFieldEditorProps = {
  template: { value: string; label?: string };
  templateType: string;
  value: any;
  onChange: (v: any) => void;
  row: any;
  resetToInherited: boolean;
  onResetToInheritedChange: (v: boolean) => void;
  formatInheritedForDisplay: (value: any, type: string) => string;
  isSameAnchorDataValue: (a: any, b: any, type: string) => boolean;
  variant: "modal" | "inline";
  onBlur?: () => void;
  inputRef?: (el: HTMLInputElement | null) => void;
  onTabNext?: () => void;
  onTabPrev?: () => void;
  autoFocus?: boolean;
  hasCellChange?: boolean;
  onSaveCell?: () => void;
  onDiscardCell?: () => void;
  isSavingCell?: boolean;
  onEmailValidationChange?: (isInvalid: boolean) => void;
  onPhoneValidationChange?: (isInvalid: boolean) => void;
  enumOptions?: { value: string; label: string }[];
  enumResetSignal?: number;
  loadOptionsForSubjectIdList?: (inputValue: string) => Promise<any[]>;
  loadOptionsForWzCodeIdList?: (inputValue: string) => Promise<any[]>;
  children?: (parts: {
    icons: ReactNode;
    input: ReactNode;
    useInheritedValueStyling: boolean;
  }) => ReactNode;
};

const AnchorDataOverviewFieldEditor: FC<AnchorDataOverviewFieldEditorProps> = ({
  template,
  templateType,
  value,
  onChange,
  row,
  resetToInherited,
  onResetToInheritedChange,
  formatInheritedForDisplay,
  isSameAnchorDataValue,
  variant,
  onBlur,
  inputRef,
  onTabNext,
  onTabPrev,
  autoFocus = true,
  hasCellChange = false,
  onSaveCell,
  onDiscardCell,
  isSavingCell = false,
  onEmailValidationChange,
  onPhoneValidationChange,
  enumOptions,
  loadOptionsForSubjectIdList,
  loadOptionsForWzCodeIdList,
  children,
}) => {
  const { t } = useTranslation();
  const ignoreNextBlurRef = useRef(false);
  const inputElRef = useRef<HTMLInputElement | null>(null);
  const [discardTooltipOpen, setDiscardTooltipOpen] = useState(false);
  const [saveTooltipOpen, setSaveTooltipOpen] = useState(false);
  const [hasPhoneValidationError, setHasPhoneValidationError] = useState(false);
  const { registerValidationTarget, focusFirstInvalidField } =
    useFocusFirstInvalidField<Record<string, never>, "field">({
      fieldOrder: [],
    });
  const fieldValidationTargetRef = registerValidationTarget("field");
  const templateId = template.value;
  const valueKey = `value_${templateId}`;
  const initialTemplateValue = row?.[valueKey];
  const isInherited = !!row?.[`is_inherited_${templateId}`];
  const hasTemplateValueObject = row?.[`anchor_data_id_${templateId}`] != null;
  const inheritedFrom = row?.[`inherited_from_${templateId}`];
  const inheritedValueForReset = row?.[`inherited_value_${templateId}`];
  const parentName = inheritedFrom?.name ?? "-";
  const isSubjectRootOfTree = !inheritedFrom;
  const isAtOriginalInheritedValue =
    isInherited &&
    isSameAnchorDataValue(value, initialTemplateValue, templateType);
  const isEmptyAndRevertingToNull =
    inheritedValueForReset == null &&
    (templateType === "subject_id_list" ||
      templateType === "wz_code_id_list") &&
    (value == null || (Array.isArray(value) && value.length === 0));
  const isResetApplied =
    resetToInherited &&
    (isEmptyAndRevertingToNull ||
      isSameAnchorDataValue(value, inheritedValueForReset, templateType));
  const showOriginalInheritedInfoState =
    !isSubjectRootOfTree && isAtOriginalInheritedValue && !resetToInherited;
  const showResetOption =
    !isSubjectRootOfTree &&
    (hasTemplateValueObject || !isAtOriginalInheritedValue) &&
    !showOriginalInheritedInfoState;
  const useInheritedValueStyling =
    showOriginalInheritedInfoState || isResetApplied;
  const resetTooltipValue = formatInheritedForDisplay(
    inheritedValueForReset,
    templateType,
  );

  useEffect(() => {
    if (!hasCellChange || isSavingCell) {
      setDiscardTooltipOpen(false);
      setSaveTooltipOpen(false);
    }
  }, [hasCellChange, isSavingCell]);

  const inheritedInputSx = useInheritedValueStyling
    ? {
        "& .MuiInputBase-input": {
          color: "var(--newWork60)",
          fontStyle: "italic",
        },
      }
    : undefined;

  const isEnumInline =
    templateType === "enum" &&
    variant === "inline" &&
    (enumOptions?.length ?? 0) > 0;
  const isSubjectIdListInline =
    templateType === "subject_id_list" &&
    variant === "inline" &&
    typeof loadOptionsForSubjectIdList === "function";
  const isWzCodeIdListInline =
    templateType === "wz_code_id_list" && variant === "inline";
  const wzCodeLoadOptions =
    typeof loadOptionsForWzCodeIdList === "function"
      ? loadOptionsForWzCodeIdList
      : () => Promise.resolve([]);
  if (
    templateType !== "str" &&
    templateType !== "int" &&
    templateType !== "float" &&
    templateType !== "price" &&
    templateType !== "percent" &&
    templateType !== "email" &&
    templateType !== "phone_number" &&
    templateType !== "date" &&
    templateType !== "multiline" &&
    !isEnumInline &&
    !isSubjectIdListInline &&
    !isWzCodeIdListInline
  ) {
    return null;
  }

  const isStringType = templateType === "str";
  const isFloatType = templateType === "float";
  const isPriceType = templateType === "price";
  const isPercentType = templateType === "percent";
  const isEmailType = templateType === "email";
  const isPhoneNumberType = templateType === "phone_number";
  const isDateType = templateType === "date";
  const isMultilineType = templateType === "multiline";
  const inheritedSelectStyles = useInheritedValueStyling
    ? {
        singleValue: (baseStyles: any) => ({
          ...baseStyles,
          color: "var(--newWork60)",
          fontStyle: "italic",
        }),
      }
    : undefined;
  const phoneDisplayValue = value == null || value === "" ? "" : String(value);
  const emailDisplayValue = value == null || value === "" ? "" : String(value);
  const hasEmailValidationError =
    isEmailType &&
    emailDisplayValue !== "" &&
    !emailValidationRegex.test(emailDisplayValue);

  const normalizedPhoneValue = normalizePhoneForComparison(value);
  const normalizedInitialPhone =
    normalizePhoneForComparison(initialTemplateValue);
  const hasInvalidPhoneState =
    isPhoneNumberType &&
    normalizedPhoneValue === "" &&
    normalizedInitialPhone !== "";
  const hasPhoneError =
    isPhoneNumberType &&
    hasCellChange &&
    (hasInvalidPhoneState || hasPhoneValidationError);

  useEffect(() => {
    if (templateType !== "email" || !onEmailValidationChange) return;
    onEmailValidationChange(hasEmailValidationError);
    return () => {
      onEmailValidationChange(false);
    };
  }, [templateType, hasEmailValidationError, onEmailValidationChange]);

  useEffect(() => {
    if (templateType !== "phone_number" || !onPhoneValidationChange) return;
    onPhoneValidationChange(hasPhoneError);
    return () => {
      onPhoneValidationChange(false);
    };
  }, [templateType, hasPhoneError, onPhoneValidationChange]);

  let inputDisplayValue = "";
  if (
    value != null &&
    value !== "" &&
    !(typeof value === "number" && Number.isNaN(value))
  ) {
    inputDisplayValue = String(value);
  }

  const iconBox = (
    <Box
      className={
        variant === "modal"
          ? styles.modalEditorModeIconBox
          : styles.inlineEditorModeIconBox
      }
    >
      {showOriginalInheritedInfoState && (
        <Tooltip
          placement="top"
          arrow
          title={
            <div className={styles.tooltipContent}>
              <span className={styles.labelinheritedInfo}>
                {t("inherited_value")}
                {": "}
              </span>
              {formatInheritedForDisplay(initialTemplateValue, templateType) ||
                "-"}
              <br />
              <span className={styles.labelinheritedInfo}>
                {t("inherited_from")}:{" "}
              </span>
              {parentName}
            </div>
          }
        >
          <span className={styles.inheritedIcon}>ⓘ</span>
        </Tooltip>
      )}
      {showResetOption &&
        (isResetApplied ? (
          <Tooltip
            title={
              <div className={styles.tooltipContent}>
                <span className={styles.labelinheritedInfo}>
                  {t("value_will_be_inherited")}
                </span>
                <br />
                <br />
                <span className={styles.labelinheritedInfo}>{t("from")}: </span>
                {parentName}
              </div>
            }
            placement="top"
            arrow
          >
            <span className={styles.lockResetIcon}>
              <LockResetIcon fontSize="small" />
            </span>
          </Tooltip>
        ) : (
          <Tooltip
            placement="top"
            arrow
            title={
              <div className={styles.tooltipContent}>
                <span className={styles.labelinheritedInfo}>
                  {t("inherited_values_click_to_reset")}
                </span>
                <br />
                <br />
                <span className={styles.labelinheritedInfo}>
                  {t("inherited_value")}:{" "}
                </span>
                {resetTooltipValue || "-"}
                <br />
                <span className={styles.labelinheritedInfo}>
                  {t("inherited_from")}:{" "}
                </span>
                {parentName}
              </div>
            }
          >
            <span
              className={styles.replayIcon}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                ignoreNextBlurRef.current = true;
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onResetToInheritedChange(true);
                const val =
                  inheritedValueForReset != null &&
                  typeof inheritedValueForReset === "object" &&
                  "value" in inheritedValueForReset
                    ? (inheritedValueForReset as { value: any }).value
                    : inheritedValueForReset;
                onChange(val);
              }}
            >
              <ReplayIcon fontSize="small" />
            </span>
          </Tooltip>
        ))}
    </Box>
  );

  const enumInput =
    isEnumInline && enumOptions ? (
      <>
        {variant === "inline" ? iconBox : null}
        <Box
          className={styles.enumInputContainer}
          onKeyDown={
            variant === "inline"
              ? (e: React.KeyboardEvent) => {
                  if (e.key === "Tab") {
                    e.preventDefault();
                    if (e.shiftKey) {
                      onTabPrev?.();
                    } else {
                      onTabNext?.();
                    }
                  } else if (e.key === "Escape" || e.key === "Enter") {
                    e.preventDefault();
                    (e.target as HTMLElement).blur();
                  }
                }
              : undefined
          }
        >
          <DropDown
            key={`enum-${templateId}`}
            options={enumOptions}
            useReduxDefaultValue
            defaultValue={
              value != null && value !== ""
                ? enumOptions.find((o) => String(o.value) === String(value)) ??
                  null
                : null
            }
            onChange={(sel: any) => {
              onChange(sel ? sel.value : "");
              if (resetToInherited) {
                onResetToInheritedChange(false);
              }
            }}
            onBlur={() => {
              setDiscardTooltipOpen(false);
              setSaveTooltipOpen(false);
              if (ignoreNextBlurRef.current) {
                ignoreNextBlurRef.current = false;
                return;
              }
              onBlur?.();
            }}
            isClearable={false} // NOTE: Change to true when the backend fully supports empty values.
            isSearchable
            customStyles={inheritedSelectStyles}
          />
        </Box>
      </>
    ) : null;

  const subjectIdListOptions = inheritedValueToSubjectIdListOptions(value);
  const subjectIdListInput =
    isSubjectIdListInline && loadOptionsForSubjectIdList ? (
      <>
        {variant === "inline" ? iconBox : null}
        <Box
          className={styles.subjectIdListInputContainer}
          onKeyDown={
            variant === "inline"
              ? (e: React.KeyboardEvent) => {
                  if (e.key === "Tab") {
                    e.preventDefault();
                    if (e.shiftKey) {
                      onTabPrev?.();
                    } else {
                      onTabNext?.();
                    }
                  } else if (e.key === "Escape" || e.key === "Enter") {
                    e.preventDefault();
                    (e.target as HTMLElement).blur();
                  }
                }
              : undefined
          }
        >
          <SubjectSearchSelect
            isMulti={true}
            isClearable={false} // NOTE: Change to true when the backend fully supports empty values.
            placeholder={t("search_placeholder")}
            value={
              Array.isArray(subjectIdListOptions) ? subjectIdListOptions : []
            }
            onChange={(selectedOption: any) => {
              const selected = Array.isArray(selectedOption)
                ? selectedOption
                : selectedOption
                ? [selectedOption]
                : [];
              onChange(selected);
              if (resetToInherited) {
                onResetToInheritedChange(false);
              }
            }}
            loadOptions={loadOptionsForSubjectIdList}
            customStyles={inheritedSelectStyles}
          />
        </Box>
      </>
    ) : null;

  const wzCodeIdListOptions = inheritedValueToWzCodeIdListOptions(value);
  const wzCodeIdListInput = isWzCodeIdListInline ? (
    <>
      {variant === "inline" ? iconBox : null}
      <Box
        className={styles.wzCodeIdListInputContainer}
        onKeyDown={
          variant === "inline"
            ? (e: React.KeyboardEvent) => {
                if (e.key === "Tab") {
                  e.preventDefault();
                  if (e.shiftKey) {
                    onTabPrev?.();
                  } else {
                    onTabNext?.();
                  }
                } else if (e.key === "Escape" || e.key === "Enter") {
                  e.preventDefault();
                  (e.target as HTMLElement).blur();
                }
              }
            : undefined
        }
      >
        <SubjectSearchSelect
          isMulti={true}
          isClearable={false} // NOTE: Change to true when the backend fully supports empty values.
          placeholder={t("search_placeholder")}
          value={Array.isArray(wzCodeIdListOptions) ? wzCodeIdListOptions : []}
          onChange={(selectedOption: any) => {
            const selected = Array.isArray(selectedOption)
              ? selectedOption
              : selectedOption
              ? [selectedOption]
              : [];
            onChange(selected);
            if (resetToInherited) {
              onResetToInheritedChange(false);
            }
          }}
          loadOptions={wzCodeLoadOptions}
          customStyles={inheritedSelectStyles}
        />
      </Box>
    </>
  ) : null;

  const phoneInput = isPhoneNumberType ? (
    <>
      {variant === "inline" ? iconBox : null}
      <MuiTelInput
        value={phoneDisplayValue}
        onChange={(newValue, info) => {
          const hasNationalNumber =
            info?.nationalNumber != null &&
            String(info.nationalNumber).trim() !== "";
          const valueToStore = hasNationalNumber ? newValue : "";
          const prevNormalized = normalizePhoneForComparison(value);
          const nextNormalized = normalizePhoneForComparison(valueToStore);
          if (prevNormalized === nextNormalized) return;

          onChange(valueToStore);
          if (resetToInherited) {
            onResetToInheritedChange(false);
          }

          const normalized = normalizePhoneForComparison(valueToStore);
          const isEmpty = normalized === "";
          const isValid =
            isEmpty ||
            (info?.countryCode
              ? matchIsValidTel(valueToStore || "", {
                  onlyCountries: [info.countryCode],
                })
              : matchIsValidTel(valueToStore || ""));
          setHasPhoneValidationError(!isValid && !isEmpty);
        }}
        onPaste={(e: React.ClipboardEvent) => {
          const text =
            e.clipboardData?.getData?.("text/plain") ??
            e.clipboardData?.getData?.("text") ??
            "";
          const normalized = normalizePastedPhoneNumber(text);
          if (normalized !== "") {
            e.preventDefault();
            onChange(normalized);
            const compare = normalizePhoneForComparison(normalized);
            const isEmpty = compare === "";
            const isValid = isEmpty || matchIsValidTel(normalized || "");
            setHasPhoneValidationError(!isValid && !isEmpty);
          }
        }}
        placeholder={t("input_your_phone_number")}
        defaultCountry="DE"
        langOfCountryName="DE"
        disableDropdown={false}
        size={variant === "inline" ? "small" : "medium"}
        className={styles.telInput}
        error={variant === "inline" && hasPhoneError}
        sx={inheritedInputSx}
        autoFocus={variant === "inline" && autoFocus}
        inputRef={(el: HTMLInputElement | null) => {
          inputElRef.current = el;
          fieldValidationTargetRef(el);
          inputRef?.(el);
        }}
        onBlur={() => {
          setDiscardTooltipOpen(false);
          setSaveTooltipOpen(false);
          if (ignoreNextBlurRef.current) {
            ignoreNextBlurRef.current = false;
            return;
          }
          onBlur?.();
        }}
        onKeyDown={
          variant === "inline"
            ? (e: React.KeyboardEvent) => {
                if (e.key === "Tab") {
                  e.preventDefault();
                  if (e.shiftKey) {
                    onTabPrev?.();
                  } else {
                    onTabNext?.();
                  }
                  return;
                }
                if (e.key === "Escape" || e.key === "Enter") {
                  e.preventDefault();
                  if (e.key === "Enter" && hasPhoneError) {
                    focusFirstInvalidField({}, ["field"]);
                    return;
                  }
                  (e.target as HTMLInputElement).blur();
                }
              }
            : undefined
        }
        // NOTE: Uncomment this when the backend fully supports empty values.
        // InputProps={{
        //   endAdornment:
        //     variant === "inline" ? (
        //       <InputAdornment position="end">
        //         <GenericButton
        //           variant="icon"
        //           color="iconBase"
        //           onMouseDown={(e) => {
        //             e.preventDefault();
        //             e.stopPropagation();
        //             ignoreNextBlurRef.current = true;
        //           }}
        //           onClick={(e) => {
        //             e.stopPropagation();
        //             e.preventDefault();
        //             onChange("");
        //             if (resetToInherited) {
        //               onResetToInheritedChange(false);
        //             }
        //           }}
        //         >
        //           <ClearIcon fontSize="small" />
        //         </GenericButton>
        //       </InputAdornment>
        //     ) : undefined,
        // }}
      />
    </>
  ) : null;

  const dateInput = isDateType ? (
    <AnchorDataOverviewDatePicker
      value={value}
      size={variant === "inline" ? "small" : "medium"}
      sx={inheritedInputSx}
      autoFocus={variant === "inline" && autoFocus}
      inputRef={(el) => {
        inputElRef.current = el;
        fieldValidationTargetRef(el);
        inputRef?.(el);
      }}
      onBlur={() => {
        setDiscardTooltipOpen(false);
        setSaveTooltipOpen(false);
        if (ignoreNextBlurRef.current) {
          ignoreNextBlurRef.current = false;
          return;
        }
        onBlur?.();
      }}
      onKeyDown={
        variant === "inline"
          ? (e) => {
              if (e.key === "Tab") {
                e.preventDefault();
                if (e.shiftKey) {
                  onTabPrev?.();
                } else {
                  onTabNext?.();
                }
                return;
              }
              if (e.key === "Escape" || e.key === "Enter") {
                e.preventDefault();
                (e.target as HTMLInputElement).blur();
              }
            }
          : undefined
      }
      startAdornment={variant === "inline" ? iconBox : undefined}
      onChange={(newValue) => {
        onChange(newValue);
        if (resetToInherited) {
          onResetToInheritedChange(false);
        }
      }}
    />
  ) : null;

  const textField = (
    <TextField
      fullWidth
      type={
        templateType === "int" ||
        templateType === "float" ||
        templateType === "percent"
          ? "number"
          : templateType === "email"
          ? "email"
          : "text"
      }
      multiline={isMultilineType}
      rows={isMultilineType ? (variant === "inline" ? 3 : 4) : undefined}
      size={variant === "inline" ? "small" : "medium"}
      value={inputDisplayValue}
      error={variant === "inline" && hasEmailValidationError}
      inputRef={(el) => {
        inputElRef.current = el;
        fieldValidationTargetRef(el);
        inputRef?.(el);
      }}
      onChange={(e) => {
        if (isStringType || isEmailType || isMultilineType) {
          onChange(e.target.value);
        } else if (isPercentType) {
          const raw = e.target.value;
          if (raw === "") {
            onChange(null);
          } else {
            const parsed = parseFloat(raw.replace(",", "."));
            onChange(Number.isNaN(parsed) ? null : parsed);
          }
        } else if (isPriceType) {
          const raw = e.target.value;
          if (raw === "") {
            onChange(null);
          } else if (/^[0-9]*([.,][0-9]*)?$/.test(raw)) {
            onChange(raw);
          }
        } else if (isFloatType) {
          const raw = e.target.value;
          if (raw === "") {
            onChange(null);
          } else {
            const parsed = parseFloat(raw.replace(",", "."));
            onChange(Number.isNaN(parsed) ? null : parsed);
          }
        } else {
          const raw = e.target.value;
          const parsed = raw === "" ? null : parseInt(raw, 10);
          onChange(Number.isNaN(parsed as number) ? null : parsed);
        }
        if (resetToInherited) {
          onResetToInheritedChange(false);
        }
      }}
      onBlur={() => {
        setDiscardTooltipOpen(false);
        setSaveTooltipOpen(false);
        if (ignoreNextBlurRef.current) {
          ignoreNextBlurRef.current = false;
          return;
        }
        onBlur?.();
      }}
      onKeyDown={
        variant === "inline"
          ? (e) => {
              if (e.key === "Tab") {
                e.preventDefault();
                if (e.shiftKey) {
                  onTabPrev?.();
                } else {
                  onTabNext?.();
                }
                return;
              }
              if (
                e.key === "Escape" ||
                (e.key === "Enter" && !isMultilineType)
              ) {
                e.preventDefault();
                if (e.key === "Enter" && hasEmailValidationError) {
                  focusFirstInvalidField({}, ["field"]);
                  return;
                }
                (e.target as HTMLInputElement).blur();
              }
            }
          : undefined
      }
      sx={inheritedInputSx}
      autoFocus={variant === "inline" && autoFocus}
      inputProps={{
        ...(templateType === "int" ||
          templateType === "float" ||
          templateType === "percent"
          ? { onWheel: blurNumberInputOnWheel }
          : {}),
        ...(templateType === "int"
          ? { step: 1 }
          : templateType === "float"
          ? { step: "any" }
          : templateType === "price"
          ? { inputMode: "decimal" as const }
          : templateType === "percent"
          ? { step: "0.01", min: 0, max: 100 }
          : {}),
        ...(variant === "inline" && hasEmailValidationError
          ? { title: t("enter_valid_email") }
          : {}),
      }}
      InputProps={{
        startAdornment: variant === "inline" ? iconBox : undefined,
        // NOTE: Uncomment this when the backend fully supports empty values.
        // Can also include isIntType || isFloatType || isPercentType
        // endAdornment: isStringType || isEmailType || isDateType || isMultilineType ? (
        endAdornment: isStringType ? (
          <InputAdornment position="end">
            <Tooltip title={t("set_to_empty")}>
              <GenericButton
                variant="icon"
                color="iconBase"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  ignoreNextBlurRef.current = true;
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onChange("");
                  if (resetToInherited) {
                    onResetToInheritedChange(false);
                  }
                }}
              >
                <ClearIcon fontSize="small" />
              </GenericButton>
            </Tooltip>
          </InputAdornment>
        ) : undefined,
      }}
    />
  );

  const inputElement = isWzCodeIdListInline
    ? wzCodeIdListInput
    : isSubjectIdListInline
    ? subjectIdListInput
    : isEnumInline
    ? enumInput
    : isPhoneNumberType
    ? phoneInput
    : isDateType
    ? dateInput
    : textField;

  if (typeof children === "function") {
    return (
      <>
        {children({
          icons: iconBox,
          input: inputElement,
          useInheritedValueStyling,
        })}
      </>
    );
  }

  if (variant === "inline") {
    const cellActionButtons =
      onSaveCell != null && onDiscardCell != null ? (
        <Box className={styles.cellActionButtonsContainer}>
          <Tooltip
            title={t("discard_cell")}
            disableHoverListener={!hasCellChange}
            open={discardTooltipOpen && hasCellChange}
            onOpen={() => setDiscardTooltipOpen(true)}
            onClose={() => setDiscardTooltipOpen(false)}
          >
            <span>
              <GenericButton
                variant="icon"
                color="iconBase"
                disabled={!hasCellChange}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  ignoreNextBlurRef.current = true;
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setDiscardTooltipOpen(false);
                  setSaveTooltipOpen(false);
                  onDiscardCell();
                  inputElRef.current?.blur();
                }}
              >
                <UndoIcon fontSize="small" />
              </GenericButton>
            </span>
          </Tooltip>
          <Tooltip
            title={
              hasPhoneError
                ? t("please_enter_valid_phone_number")
                : hasEmailValidationError
                ? t("enter_valid_email")
                : t("save_cell")
            }
            disableHoverListener={!hasCellChange || isSavingCell}
            open={saveTooltipOpen && hasCellChange && !isSavingCell}
            onOpen={() => {
              if (!hasCellChange || isSavingCell) return;
              setSaveTooltipOpen(true);
            }}
            onClose={() => setSaveTooltipOpen(false)}
          >
            <span>
              <GenericButton
                variant="icon"
                color="primary"
                disabled={!hasCellChange || isSavingCell}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  ignoreNextBlurRef.current = true;
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  if (hasEmailValidationError || hasPhoneError) {
                    focusFirstInvalidField({}, ["field"]);
                    return;
                  }
                  setSaveTooltipOpen(false);
                  setDiscardTooltipOpen(false);
                  onSaveCell();
                  inputElRef.current?.blur();
                }}
              >
                <SaveIcon fontSize="small" />
              </GenericButton>
            </span>
          </Tooltip>
        </Box>
      ) : null;

    return (
      <Box className={styles.inlineEditorContainer}>
        {cellActionButtons}
        {inputElement}
      </Box>
    );
  }

  return (
    <>
      {iconBox}
      {inputElement}
    </>
  );
};

export default AnchorDataOverviewFieldEditor;
